import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Shield, 
  MoreVertical,
  Search,
  CheckCircle,
  Clock,
  XCircle 
} from 'lucide-react';
import { Permission, UserRole } from '@/types';
import ProtectedRoute, { PermissionGate } from '@/components/ProtectedRoute';

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  status: 'active' | 'pending' | 'inactive';
  joinedDate: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: UserRole;
  invitedBy: string;
  invitedDate: string;
  status: 'pending' | 'expired';
}

export default function TeamManagement() {
  const { user } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data - replace with API calls
  const [teamMembers] = useState<TeamMember[]>([
    {
      id: '1',
      firstName: 'Jane',
      lastName: 'Smith',
      email: 'jane.smith@company.com',
      role: 'org_owner',
      status: 'active',
      joinedDate: '2024-01-15',
    },
    {
      id: '2',
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@company.com',
      role: 'recruiter',
      status: 'active',
      joinedDate: '2024-02-20',
    },
    {
      id: '3',
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.j@company.com',
      role: 'hr_admin',
      status: 'active',
      joinedDate: '2024-03-10',
    },
  ]);

  const [pendingInvites] = useState<PendingInvite[]>([
    {
      id: '1',
      email: 'mike.wilson@company.com',
      role: 'recruiter',
      invitedBy: 'Jane Smith',
      invitedDate: '2024-11-05',
      status: 'pending',
    },
  ]);

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'org_owner':
        return 'bg-primary-100 text-primary-800';
      case 'hr_admin':
        return 'bg-orange-100 text-orange-800';
      case 'hiring_manager':
        return 'bg-purple-100 text-purple-800';
      case 'recruiter':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRole = (role: string) => {
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'inactive':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const filteredMembers = teamMembers.filter(member =>
    `${member.firstName} ${member.lastName} ${member.email}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <ProtectedRoute allowedRoles={['hr_admin', 'org_owner']} requiredPermissions={[Permission.VIEW_USERS]}>
      <DashboardLayout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage your organization's team members and invitations
              </p>
            </div>
            <PermissionGate requiredPermission={Permission.INVITE_USER}>
              <Button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Team Member
              </Button>
            </PermissionGate>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Members</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{teamMembers.length}</p>
                </div>
                <Users className="w-8 h-8 text-primary-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending Invites</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{pendingInvites.length}</p>
                </div>
                <Mail className="w-8 h-8 text-yellow-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Members</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">
                    {teamMembers.filter(m => m.status === 'active').length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search team members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Team Members List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
            </div>

            <div className="divide-y divide-gray-200">
              {filteredMembers.map((member) => (
                <div key={member.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                        {member.firstName[0]}{member.lastName[0]}
                      </div>
                      
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">
                            {member.firstName} {member.lastName}
                          </p>
                          {member.id === user?.id && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                              You
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{member.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                        <Shield className="w-3 h-3 mr-1" />
                        {formatRole(member.role)}
                      </span>

                      <div className="flex items-center space-x-2">
                        {getStatusIcon(member.status)}
                        <span className="text-xs text-gray-600 capitalize">{member.status}</span>
                      </div>

                      <PermissionGate requiredPermission={Permission.EDIT_USER}>
                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                          <MoreVertical className="w-4 h-4 text-gray-400" />
                        </button>
                      </PermissionGate>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pending Invitations */}
          {pendingInvites.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Pending Invitations</h2>
              </div>

              <div className="divide-y divide-gray-200">
                {pendingInvites.map((invite) => (
                  <div key={invite.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <Mail className="w-8 h-8 text-yellow-600" />
                        
                        <div>
                          <p className="text-sm font-medium text-gray-900">{invite.email}</p>
                          <p className="text-xs text-gray-600">
                            Invited by {invite.invitedBy} on {new Date(invite.invitedDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(invite.role)}`}>
                          {formatRole(invite.role)}
                        </span>

                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-yellow-600" />
                          <span className="text-xs text-yellow-600">Pending</span>
                        </div>

                        <PermissionGate requiredPermission={Permission.DELETE_USER}>
                          <Button variant="outline" size="sm">
                            Resend
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                            Revoke
                          </Button>
                        </PermissionGate>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invite Modal */}
          {showInviteModal && (
            <InviteModal
              onClose={() => setShowInviteModal(false)}
              orgName={user?.company || 'Your Organization'}
            />
          )}
        </div>
      </DashboardLayout>
    </ProtectedRoute>
  );
}

// Invite Modal Component
interface InviteModalProps {
  onClose: () => void;
  orgName: string;
}

function InviteModal({ onClose, orgName }: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('recruiter');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // TODO: API call to send invitation
      // await apiClient.inviteTeamMember({ email, role });
      
      alert(`Invitation sent to ${email}`);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Invite Team Member</h2>
        <p className="text-sm text-gray-600 mb-6">
          Send an invitation to join {orgName}
        </p>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleInvite} className="space-y-4">
          <Input
            label="Email Address"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="recruiter">Recruiter</option>
              <option value="hiring_manager">Hiring Manager</option>
              <option value="hr_admin">HR Administrator</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Select the role for this team member
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isLoading}
            >
              Send Invitation
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
